/**
 * Per-connection display names. The Hub API (`label` on /api/mail/accounts) is the
 * source of truth across devices; localStorage mirrors it for synchronous reads.
 */

import { api as hubApi, type MailAccount } from "@/lib/api";

export const CONNECTION_DISPLAY_NAMES_KEY = "atw-hub-connection-display-names";
export const CONNECTION_DISPLAY_NAMES_EVENT = "atw-hub-connection-display-names";
/** Accounts whose name this device has confirmed on the server. */
const CONNECTION_DISPLAY_NAMES_SYNCED_KEY = "atw-hub-connection-display-names-synced";

type DisplayNameMap = Record<string, string>;

function readSynced(): Set<string> {
  try {
    const parsed = JSON.parse(localStorage.getItem(CONNECTION_DISPLAY_NAMES_SYNCED_KEY) || "[]") as unknown;
    return new Set(Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === "string") : []);
  } catch {
    return new Set();
  }
}

function writeSynced(synced: Set<string>) {
  try {
    localStorage.setItem(CONNECTION_DISPLAY_NAMES_SYNCED_KEY, JSON.stringify([...synced]));
  } catch {
    /* ignore quota */
  }
}

function markSynced(key: string) {
  const synced = readSynced();
  if (synced.has(key)) return;
  synced.add(key);
  writeSynced(synced);
}

function serverLabel(account: Pick<MailAccount, "label">): string {
  return typeof account.label === "string" ? account.label.trim() : "";
}

function readMap(): DisplayNameMap {
  try {
    const raw = localStorage.getItem(CONNECTION_DISPLAY_NAMES_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const out: DisplayNameMap = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value === "string" && value.trim()) out[key] = value.trim();
    }
    return out;
  } catch {
    return {};
  }
}

function writeMap(map: DisplayNameMap) {
  try {
    localStorage.setItem(CONNECTION_DISPLAY_NAMES_KEY, JSON.stringify(map));
  } catch {
    /* ignore quota */
  }
  window.dispatchEvent(new CustomEvent(CONNECTION_DISPLAY_NAMES_EVENT, { detail: map }));
}

function emailKey(email?: string): string | null {
  const normalized = (email || "").trim().toLowerCase();
  return normalized ? `email:${normalized}` : null;
}

/**
 * Local label (mirrors the server after each accounts fetch, and updates instantly on
 * rename), else server label, else API displayName, else email.
 */
export function resolveAccountLabel(
  account: Pick<MailAccount, "id" | "email" | "displayName" | "label">,
): string {
  const map = readMap();
  const byId = map[account.id]?.trim();
  if (byId) return byId;
  const byEmail = emailKey(account.email);
  if (byEmail) {
    const named = map[byEmail]?.trim();
    if (named) return named;
  }
  const fromServer = serverLabel(account);
  if (fromServer) return fromServer;
  // Live payloads sometimes send displayName as a contact object — never call .trim() on it.
  const rawName = account.displayName as unknown;
  const apiName =
    typeof rawName === "string"
      ? rawName.trim()
      : rawName && typeof rawName === "object"
        ? String(
            (rawName as { name?: unknown; email?: unknown }).name ||
              (rawName as { email?: unknown }).email ||
              "",
          ).trim()
        : "";
  if (apiName) return apiName;
  return (account.email || "").trim();
}

export function getStoredConnectionDisplayName(
  account: Pick<MailAccount, "id" | "email">,
): string {
  const map = readMap();
  const byId = map[account.id]?.trim();
  if (byId) return byId;
  const byEmail = emailKey(account.email);
  if (byEmail) return map[byEmail]?.trim() || "";
  return "";
}

export function setConnectionDisplayName(
  accountId: string,
  email: string | undefined,
  name: string,
) {
  const map = readMap();
  const trimmed = name.trim();
  const ek = emailKey(email);
  if (trimmed) {
    map[accountId] = trimmed;
    if (ek) map[ek] = trimmed;
  } else {
    delete map[accountId];
    if (ek) delete map[ek];
  }
  writeMap(map);
}

/** Best-effort API persist; local prefs still apply if the host has no PATCH. */
export async function persistConnectionDisplayName(
  accountId: string,
  email: string | undefined,
  name: string,
  api: <T = unknown>(path: string, init?: RequestInit) => Promise<T>,
): Promise<void> {
  setConnectionDisplayName(accountId, email, name);
  const trimmed = name.trim();
  try {
    await api(`/api/mail/accounts/${encodeURIComponent(accountId)}`, {
      method: "PATCH",
      body: JSON.stringify({ displayName: trimmed || null }),
    });
    markSynced(emailKey(email) || accountId);
  } catch {
    /* local prefs already saved */
  }
}

/**
 * Mirror server inbox names into local prefs after GET /api/mail/accounts.
 * A name only this device knows is uploaded once; after the server has confirmed
 * it, a missing server label means it was cleared on another device.
 */
export function applyServerAccountLabels(accounts: MailAccount[]): void {
  const map = readMap();
  const synced = readSynced();
  let changed = false;
  for (const account of accounts) {
    const ek = emailKey(account.email);
    const syncKey = ek || account.id;
    const fromServer = serverLabel(account);
    const local = (map[account.id] || (ek ? map[ek] : "") || "").trim();
    if (fromServer) {
      if (map[account.id] !== fromServer || (ek && map[ek] !== fromServer)) {
        map[account.id] = fromServer;
        if (ek) map[ek] = fromServer;
        changed = true;
      }
      synced.add(syncKey);
    } else if (local && synced.has(syncKey)) {
      delete map[account.id];
      if (ek) delete map[ek];
      changed = true;
    } else if (local) {
      void hubApi(`/api/mail/accounts/${encodeURIComponent(account.id)}`, {
        method: "PATCH",
        body: JSON.stringify({ displayName: local }),
      })
        .then(() => markSynced(syncKey))
        .catch(() => {
          /* older API without PATCH — keep the local name */
        });
    }
  }
  writeSynced(synced);
  if (changed) writeMap(map);
}
